import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Alert,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import api from "../../services/api";
import OrderFilters from "./OrderFilters";
import OrderList from "./OrderList";
import LoadMoreButton from "./LoadMoreButton";
import { useSnackbarAlert } from "../../hooks/useSnackbarAlert";
import { useCart } from "../../context/CartContext";

const OrderHistoryPage = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [visibleCount, setVisibleCount] = useState(5);
  const [showFilters, setShowFilters] = useState(false);
  const [legacyOrderCount, setLegacyOrderCount] = useState(0);
  const [showLegacyBanner, setShowLegacyBanner] = useState(true);

  const snackbar = useSnackbarAlert();
  const { addToCart } = useCart();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    document.title = "My Orders";
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchLegacyOrderCount();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [statusFilter, dateRange, orders]);

  const fetchOrders = async () => {
    try {
      const response = await api.get("/orders/my-orders");

      const updatedOrders = response.data.map((order) => {
        if (Array.isArray(order.edits) && order.edits.length > 0) {
          const latestEdit = order.edits[order.edits.length - 1].orderSnapshot;
          return { ...order, ...latestEdit };
        }
        return order;
      });

      setOrders(updatedOrders);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    }
  };

  const fetchLegacyOrderCount = async () => {
    try {
      const res = await api.get("/legacy-orders/my-orders-count");
      const count = res.data.count || 0;
      setLegacyOrderCount(count);
      setShowLegacyBanner(count > 0);
    } catch (err) {
      console.error("Failed to fetch legacy order count:", err);
    }
  };

  const applyFilters = () => {
    let result = [...orders];

    if (statusFilter !== "all") {
      result = result.filter((order) => order.meta.status === statusFilter);
    }

    if (dateRange.start && dateRange.end) {
      result = result.filter((order) => {
        const date = new Date(order.meta.createdAt);
        return date >= dateRange.start && date <= dateRange.end;
      });
    }

    setFilteredOrders(result);
  };

  const handleReorder = (order) => {
    order.items.forEach((item) => {
      addToCart({
        _id: item.productId,
        name: item.name,
        image: item.image,
        quantity: item.quantity,
        unitPrice: item.price,
        type: item.type,
        flavor: item.flavor,
        size: item.size,
        customization: item.customization || {},
        category: "Reorder",
      });
    });

    snackbar.success("Items added to cart!");
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {legacyOrderCount > 0 && showLegacyBanner && (
        <Alert
          severity="info"
          onClose={() => setShowLegacyBanner(false)}
          sx={{
            mb: 3,
            backgroundColor: "#FFF9E1",
            border: "1px solid #F9D78F",
          }}>
          You have {legacyOrderCount} archived{" "}
          {legacyOrderCount > 1 ? "orders" : "order"} from our previous system.{" "}
          <a
            href="/legacy-orders"
            style={{ fontWeight: 600, color: "#ca7c00" }}>
            View Legacy Orders →
          </a>
        </Alert>
      )}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        sx={{ mb: isMobile ? 0 : 4 }}>
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
          My Orders ({filteredOrders.length})
        </Typography>

        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          onClick={() => setShowFilters((prev) => !prev)}
          sx={{
            cursor: "pointer",
            color: "primary.main",
            display: { xs: "flex", sm: "none" },
          }}>
          <FilterListIcon fontSize="medium" />
          <Typography variant="body1" fontWeight={500}>
            Filter
          </Typography>
        </Stack>
      </Stack>

      <OrderFilters
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        dateRange={dateRange}
        setDateRange={setDateRange}
        expanded={!isMobile || showFilters}
      />

      <OrderList
        orders={filteredOrders.slice(0, visibleCount)}
        onReorder={handleReorder}
      />

      {filteredOrders.length > visibleCount && (
        <LoadMoreButton onClick={() => setVisibleCount((prev) => prev + 5)} />
      )}
    </Container>
  );
};

export default OrderHistoryPage;
